"use client";

import { useEffect, useState, useMemo, useRef } from "react";
import { UsersRound } from "lucide-react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme } from "@mui/material/styles";

import { cn } from "@/lib/utils";
import { createAISlideshowImageList } from "@/utils/promptAnalyzer";
import Spinner from "@/components/common/Spinner";

export type ImageGenCarouselProps = {
  statusText: string;
  prompt?: string;
};

const ImageGenCarousel: React.FC<ImageGenCarouselProps> = ({
  statusText,
  prompt = "",
}) => {
  const isMobile = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down("md")
  );
  const validPrompt = prompt?.trim() || "creative digital artwork";

  const [imageData, setImageData] = useState<{
    images: string[];
    categories: string[];
  }>({
    images: [],
    categories: [],
  });
  const [isLoading, setIsLoading] = useState(true);

  const [translateX, setTranslateX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [dragStartTranslateX, setDragStartTranslateX] = useState(0);
  const sliderContainerRef = useRef<HTMLDivElement>(null);
  const autoScrollPausedRef = useRef<boolean>(false);
  const currentPositionRef = useRef<number>(0);

  const IMAGE_WIDTH = 280;
  const IMAGE_GAP = 20;
  const ITEM_WIDTH = IMAGE_WIDTH + IMAGE_GAP;

  useEffect(() => {
    async function loadImages() {
      try {
        setIsLoading(true);
        const result = await createAISlideshowImageList(validPrompt, []);
        setImageData(result);
      } catch (error) {
        console.error("Error with AI analysis:", error);
        setImageData({ images: [], categories: [] });
      } finally {
        setIsLoading(false);
      }
    }

    loadImages();
  }, [validPrompt]);

  const imageUrls = imageData.images;

  const extendedImages = useMemo(() => {
    return [...imageUrls, ...imageUrls, ...imageUrls];
  }, [imageUrls]);

  const cycleWidth = imageUrls.length * ITEM_WIDTH;

  useEffect(() => {
    currentPositionRef.current = translateX;
  }, [translateX]);

  const handleStart = (clientX: number) => {
    setIsDragging(true);
    setStartX(clientX);
    setDragStartTranslateX(currentPositionRef.current); // Use current position
    autoScrollPausedRef.current = true;
  };

  const handleMove = (clientX: number) => {
    if (!isDragging) return;

    const deltaX = clientX - startX;
    const newTranslateX = dragStartTranslateX + deltaX;

    setTranslateX(newTranslateX);
  };

  const handleEnd = () => {
    if (!isDragging) return;

    setIsDragging(false);

    // Resume auto-scroll after a short delay
    setTimeout(() => {
      autoScrollPausedRef.current = false;
    }, 50);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    handleStart(e.clientX);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    e.preventDefault();
    handleMove(e.clientX);
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    e.preventDefault();
    handleEnd();
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    handleStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;

    // Only prevent default if we're actually dragging horizontally
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - startX);

    if (deltaX > 5) {
      // Add threshold to avoid preventing small movements
      e.preventDefault();
    }

    handleMove(touch.clientX);
  };

  const handleTouchEnd = () => {
    handleEnd();
  };

  // Auto-scroll animation
  useEffect(() => {
    let animationFrameId: number;
    const animationSpeed = isMobile ? 0.3 : 0.5; // Pixels per frame

    const animate = () => {
      if (!autoScrollPausedRef.current && !isDragging) {
        setTranslateX((prev) => {
          const newPosition = prev - animationSpeed;
          // Only normalize when we've scrolled far enough
          if (newPosition < -cycleWidth * 1.5) {
            return newPosition + cycleWidth;
          }
          return newPosition;
        });
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isMobile, cycleWidth, isDragging]);

  return (
    <div className="tw:flex tw:flex-col tw:w-full tw:max-w-[650px] tw:py-2 tw:gap-6 tw:lg:gap-8">
      <div className="tw:flex tw:flex-col tw:gap-4">
        <div className="tw:flex tw:items-center tw:justify-between">
          <h4 className="tw:text-xl tw:font-medium">
            Crafting Your{" "}
            <span className="tw:text-primary-red">Masterpiece</span>
          </h4>
          <Spinner size={20} />
        </div>
        <div className="tw:flex tw:items-center tw:gap-1.5">
          <UsersRound size={12} className="tw:text-[#838383]" />
          <p className="tw:text-xxs tw:text-foreground-primary">
            Community Creations
          </p>
        </div>
        <div className="tw:w-full tw:relative tw:rounded-sm tw:shadow-[0px_0px_2px_0px_rgba(0,0,0,0.1)] tw:border tw:border-neutral-200 tw:overflow-hidden">
          <div
            className={cn(
              "tw:h-full tw:max-h-[250px] tw:flex tw:items-center tw:overflow-hidden tw:relative tw:cursor-grab tw:[touch-action:pan-y] tw:before:content-[''] tw:before:absolute tw:before:top-0 tw:before:bottom-0 tw:before:w-[10%] tw:before:left-0 tw:before:pointer-events-none tw:before:z-10 tw:before:bg-[linear-gradient(to_right,rgba(240,240,240,1)_0%,rgba(240,240,240,0.8)_5%,rgba(240,240,240,0)_100%)] tw:after:content-[''] tw:after:absolute tw:after:top-0 tw:after:bottom-0 tw:after:w-[10%] tw:after:right-0 tw:after:pointer-events-none tw:after:z-10 tw:after:bg-[linear-gradient(to_left,rgba(240,240,240,1)_0%,rgba(240,240,240,0.8)_10%,rgba(240,240,240,0)_100%)]",
              { "tw:select-none tw:cursor-grabbing": isDragging }
            )}
            ref={sliderContainerRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {isLoading ? (
              <div className="tw:w-full tw:flex tw:items-center tw:justify-center">
                <p className="tw:text-gray-500">Loading images...</p>
              </div>
            ) : (
              <div
                className="tw:flex tw:gap-5 tw:pointer-events-none tw:will-change-transform"
                style={{
                  transform: `translateX(${translateX}px)`,
                  transition: "none",
                }}
              >
                {extendedImages.map((url, index) => (
                  <img
                    key={`${index}-${url}`}
                    src={url}
                    alt={`AI Generated Image ${index + 1}`}
                    className="tw:flex-shrink-0 tw:w-[280px] tw:h-[250px] tw:object-cover tw:rounded-sm tw:shadow-[0_4px_20px_rgba(0,0,0,0.15)] tw:transition-all tw:duration-300 tw:ease-[ease] tw:select-none tw:[-webkit-user-drag:none] tw:[-khtml-user-drag:none] tw:[-moz-user-drag:none] tw:[-o-user-drag:none] tw:[user-drag:none] tw:hover:scale-105 tw:hover:shadow-[0_8px_30px_rgba(0,0,0,0.25)]"
                    loading="lazy"
                    draggable="false"
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="tw:hidden tw:text-center tw:lg:tw:block">
        <p className="tw:text-gray-500 tw:text-base tw:max-w-[400px] tw:mx-auto">
          {statusText}
        </p>
      </div>
    </div>
  );
};

export default ImageGenCarousel;
